import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth, formatApiError } from "@/lib/auth";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (auth.isAuthed()) return <Navigate to="/admincontrol" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await auth.login(email.trim(), password);
      nav("/admincontrol", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-bone flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-muted2 hover:text-forest mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="bg-white border border-black/5 p-10 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-forest/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-forest" strokeWidth={1.5} />
            </div>
            <div>
              <div className="overline text-terracotta">Admin</div>
              <h1 className="font-display text-2xl font-medium text-ink">
                Novacore console
              </h1>
            </div>
          </div>

          <form onSubmit={submit} noValidate className="space-y-5">
            <div>
              <Label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-muted2">
                Username
              </Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin"
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                autoFocus
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="admin-pw" className="text-xs uppercase tracking-[0.2em] text-muted2">
                Password
              </Label>
              <Input
                id="admin-pw"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
            </div>

            {err && (
              <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 border border-red-200">
                {err}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="btn-primary w-full justify-center h-12 disabled:opacity-70"
            >
              {busy ? (
                <>Signing in <Loader2 className="w-4 h-4 animate-spin" /></>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        <p className="text-xs text-muted2 mt-6 text-center">
          Restricted area — authorised users only.
        </p>
      </div>
    </div>
  );
}
